// ============================================================================
// route-week-math — pure parity / week-of-month math for the My Route week
// views (This Week + Next Week). No React, no Supabase — just dates and the
// A/B rotation rules, so it can be unit-tested in isolation.
//
// The rotation model (verified live 2026-05-30):
//   * Each AM has an am_route_week_state row: current_parity ('A'|'B') and
//     effective_week_start (the Monday that parity took effect).
//   * v_am_routes_week rows carry cadence + week_parity (+ week_of_month for
//     monthly stops). A stop appears in a given week when its cadence rule
//     matches that week's (parity, weekOfMonth):
//       - Weekly      → every week (week_parity is null), except any
//                       week-of-month listed in exclude_weeks.
//       - Biweekly    → only on its parity week. 'A_B' means both weeks.
//       - Monthly     → only on its week_of_month (null = unscheduled →
//                       shown every week so it is never silently dropped).
//       - Bimonthly / As_Needed → not day-scheduled; surfaced in the
//                       "as able" list, never the Mon–Sun grid.
// ============================================================================

export type Parity = 'A' | 'B';

export interface WeekState {
  current_parity: Parity | null;
  /** Monday (local YYYY-MM-DD) the current parity took effect. */
  effective_week_start: string | null;
}

export interface WeekTarget {
  /** The A/B parity that the target week resolves to. */
  parity: Parity;
  /** 1-based ordinal of this weekday within the month (1st..5th). */
  weekOfMonth: number;
  /** Monday (local YYYY-MM-DD) of the target week. */
  weekStart: string;
}

// Minimal shape the grid filter needs from a v_am_routes_week / template row.
export interface WeekRuleRow {
  cadence: string | null;
  week_parity: string | null;
  week_of_month: number | null;
  is_as_able?: boolean | null;
  /**
   * Week-of-month ordinals (1–5) on which a *Weekly* stop is suppressed.
   * Lets a weekly backbone be hidden on specific weeks (e.g. a Week-1 circuit
   * replaces the weekly route). Empty / null = runs every week.
   */
  exclude_weeks?: number[] | null;
}

// --- date helpers (local, not UTC — matches the AM's calendar day) ---------

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Local YYYY-MM-DD. */
export function localISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Monday of the week containing `d`, local. */
export function mondayOf(d: Date): Date {
  const base = startOfDay(d);
  const dow = base.getDay(); // 0 Sun .. 6 Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  base.setDate(base.getDate() + diff);
  return base;
}

/** Whole weeks between two Mondays (b - a), rounded to the nearest 7 days. */
export function weeksBetween(a: Date, b: Date): number {
  const ms = mondayOf(b).getTime() - mondayOf(a).getTime();
  return Math.round(ms / (7 * 24 * 60 * 60 * 1000));
}

/**
 * 1-based ordinal of this weekday within its month — the "1st Monday",
 * "2nd Monday" sense used for monthly cadence. day 1–7 → 1, 8–14 → 2, …
 */
export function weekOfMonth(d: Date): number {
  return Math.floor((d.getDate() - 1) / 7) + 1;
}

// --- parity resolution -----------------------------------------------------

export function otherParity(p: Parity): Parity {
  return p === 'A' ? 'B' : 'A';
}

/**
 * Parity for the week containing `forDate`, given the AM's stored state.
 * Parity alternates A↔B every calendar week from effective_week_start.
 * Falls back to the stored current_parity (or 'A') when no anchor is set.
 */
export function parityForWeek(state: WeekState, forDate: Date): Parity {
  const current = state.current_parity ?? 'A';
  if (!state.effective_week_start) return current;
  const anchor = new Date(`${state.effective_week_start}T00:00:00`);
  if (Number.isNaN(anchor.getTime())) return current;
  const delta = weeksBetween(anchor, forDate);
  // Even number of weeks from the anchor → same parity; odd → flipped.
  const flipped = Math.abs(delta) % 2 === 1;
  return flipped ? otherParity(current) : current;
}

/** Target descriptor for the week containing `today` (default: now). */
export function thisWeekTarget(state: WeekState, today: Date = new Date()): WeekTarget {
  const monday = mondayOf(today);
  return {
    parity: parityForWeek(state, today),
    weekOfMonth: weekOfMonth(today),
    weekStart: localISO(monday),
  };
}

/** Target descriptor for the week *after* the one containing `today`. */
export function nextWeekTarget(state: WeekState, today: Date = new Date()): WeekTarget {
  const nextMonday = mondayOf(today);
  nextMonday.setDate(nextMonday.getDate() + 7);
  return {
    parity: parityForWeek(state, nextMonday),
    weekOfMonth: weekOfMonth(nextMonday),
    weekStart: localISO(nextMonday),
  };
}

// --- stop ↔ week matching --------------------------------------------------

/**
 * Does a v_am_routes_week row belong in the Mon–Sun grid for `target`?
 * As-able rows (no fixed day) are excluded — they render in their own list.
 */
export function stopMatchesWeek(row: WeekRuleRow, target: WeekTarget): boolean {
  if (row.is_as_able) return false;
  const cadence = row.cadence ?? '';
  switch (cadence) {
    case 'Weekly':
      // Weekly = every week, unless this week-of-month is explicitly excluded
      // (e.g. a Week-1 circuit replaces the weekly backbone that week).
      if (row.exclude_weeks && row.exclude_weeks.includes(target.weekOfMonth)) {
        return false;
      }
      return true;
    case 'Biweekly':
      // 'A_B' runs both weeks; otherwise only on the matching parity week.
      return row.week_parity === 'A_B' || row.week_parity === target.parity;
    case 'Monthly':
      // Unscheduled (null) monthly stops are shown every week so they are
      // never silently dropped; scheduled ones match their week_of_month.
      return row.week_of_month == null || row.week_of_month === target.weekOfMonth;
    default:
      // Bimonthly / As_Needed / unknown → not day-scheduled.
      return false;
  }
}
